import React, { ChangeEvent, useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Autocomplete } from '@mui/material';
import { allergyMasters } from '../masterinterface';
import { textFieldStyles } from '../../../components/Forms/textfield';
import CustomButton from '../../../components/Button/DialogButtons';
import SnackbarMessage from '../../../components/Snackbar';
import { patchRequest, postRequest } from '../baseservice';

interface AllergyMasterDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: allergyMasters) => void;
    providerid: string;
    selectedAllergy: allergyMasters | null;
}

const AllergyMasterDialog: React.FC<AllergyMasterDialogProps> = ({
    open,
    onClose,
    onSave,
    providerid,
    selectedAllergy
}) => {
    console.log('providerid', providerid);
    // Form data state
    const [formData, setFormData] = useState<allergyMasters>({
        id: 0,
        provider_id: 0,
        master_type_slug: 'allergy',
        name: '',
        slug: '',
        attributes: {
            allergy_type: '',
            allergy_category: '',
        },
        is_active: 1,
    });

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('error');

    // Allergy types and categories
    const allergyType = [
        { label: 'Cereals', value: 'Cereals' },
        { label: 'Dairy', value: 'Dairy' },
        { label: 'Fruits', value: 'Fruits' },
        { label: 'Meat', value: 'Meat' },
        { label: 'Misc', value: 'Misc' },
        { label: 'Nuts', value: 'Nuts' },
        { label: 'Poultry', value: 'Poultry' },
        { label: 'Spices', value: 'Spices' },
        { label: 'Sea Food', value: 'Sea Food' },
        { label: 'Outside', value: 'Outside' },
        { label: 'Vegetables', value: 'Vegetables' },
    ];

    const allergyCategory = [
        { label: 'Food', value: 'Food' },
        { label: 'Environment', value: 'Environment' },
    ];

    // Reset form data
    const resetFormData = () => {
        setFormData({
            id: 0,
            provider_id: 0,
            master_type_slug: 'allergy',
            name: '',
            slug: '',
            attributes: {
                allergy_type: '',
                allergy_category: '',
            },
            is_active: 1,
        });
    };

    // Handle text field changes
    const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // Handle changes for Autocomplete (Allergy Type and Category)
    const handleChange = (event: any, value: any, field: string) => {
        setFormData((prevData) => ({
            ...prevData,
            attributes: {
                ...prevData.attributes,
                [field]: value ? value.value : '',
            },
        }));
    };

    // Save form data
    const handleSave = async () => {
        try {
            const url = `resource/custom_masters/${formData.id !== 0 ? formData.id : ''}`;
            const requestData = {
                name: formData.name,
                slug: formData.slug,
                master_type_slug: formData.master_type_slug,
                is_active: formData.is_active,
                provider_id: providerid,
                attributes: formData.attributes,
            };

            // Perform PATCH if we're editing, otherwise POST
            const response = formData.id !== 0
                ? await patchRequest('resource/custom_masters/' + formData.id, requestData)
                : await postRequest('resource/custom_masters', requestData);

            if (response) {
                onSave(formData); // Callback after saving
                setSnackbarMessage(formData.id !== 0 ? 'Allergy updated successfully!' : 'Allergy saved successfully!');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                resetFormData();
                onClose(); // Close dialog after save
            }
        } catch (error: any) {
            // Error handling
            if (error.response) {
                const errorMessage = error.response?.data?.data?.errors?.name || 'Unknown error occurred';
                setSnackbarMessage(errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1));
            } else if (error.request) {
                setSnackbarMessage('No response received from the server.');
            } else {
                setSnackbarMessage('An unexpected error occurred.');
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true); // Open error snackbar
        }
    };

    // Handle closing of dialog
    const handleDialogClose = () => {
        resetFormData(); // Reset the form when closing
        onClose();
    };

    // Handle Snackbar close
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Populate form data if selected allergy exists (edit mode)
    useEffect(() => {
        if (selectedAllergy) {
            setFormData(selectedAllergy);
        } else {
            resetFormData();
        }
    }, [selectedAllergy]);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ textAlign: 'center', fontFamily: 'Jost, sans-serif' }} className="text-center text-white font-bold bg-primary">
                    {selectedAllergy ? 'Edit Allergy Masters' : 'Add New Allergy Masters'}
                </DialogTitle>
                <DialogContent className="mt-4 p-4">
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <div>
                                <h2 style={{ fontFamily: 'Jost, sans-serif', color: 'gray' }}>Name</h2>
                                <TextField
                                    placeholder="Enter Allergy Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleTextFieldChange}
                                    fullWidth
                                    sx={textFieldStyles}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <div>
                                <h2 style={{ fontFamily: 'Jost, sans-serif', color: 'gray' }}>Code</h2>
                                <TextField
                                    placeholder="Enter Allergy Slug"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleTextFieldChange}
                                    fullWidth
                                    sx={textFieldStyles}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <div>
                                <h2 style={{ fontFamily: 'Jost, sans-serif', color: 'gray' }}>Allergy Type</h2>
                                <Autocomplete
                                    options={allergyType}
                                    getOptionLabel={(option) => option.label}
                                    onChange={(event, value) => handleChange(event, value, 'allergy_type')}
                                    value={allergyType.find(option => option.value === formData.attributes.allergy_type) || null}
                                    renderInput={(params) => <TextField {...params} placeholder="Select Type" sx={textFieldStyles} />}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <div>
                                <h2 style={{ fontFamily: 'Jost, sans-serif', color: 'gray' }}>Allergy Category</h2>
                                <Autocomplete
                                    options={allergyCategory}
                                    getOptionLabel={(option) => option.label}
                                    onChange={(event, value) => handleChange(event, value, 'allergy_category')}
                                    value={allergyCategory.find(option => option.value === formData.attributes.allergy_category) || null}
                                    renderInput={(params) => <TextField {...params} placeholder="Select Category" sx={textFieldStyles} />}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <CustomButton onClick={handleDialogClose} color="secondary">Cancel</CustomButton>
                    <CustomButton onClick={handleSave} color="primary"> {selectedAllergy ? 'Update' : 'Save'}</CustomButton>
                </DialogActions>
            </Dialog>

            {/* Snackbar for success or error messages */}
            <SnackbarMessage
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleSnackbarClose}
            />
        </>
    );
};

export default AllergyMasterDialog;
